import { Button } from "@/components/ui/button";
import { useMentorView } from "@/lib/hooks/admin/use-mentor-view.ts";
import { useShouldShowFloatingChat } from "@/lib/hooks/admin/use-should-show-floating-chat.ts";
import { useGlobalChats } from "@/lib/hooks/conversations/use-global-chats.ts";
import { useActiveCallNotification } from "@/lib/hooks/conversations/useActiveCallNotification.tsx";
import { usePopupStore } from "@/lib/hooks/conversations/use-popup-store.ts";
import { cn } from "@/lib/utils.ts";
import { ChatBubbleIcon } from "@radix-ui/react-icons";
import { useFeatureFlagEnabled } from "@posthog/react";
import { useLocation } from "@tanstack/react-router";
import { X } from "lucide-react";
import { useCallback } from "react";
import { ChatPopup } from "./chat-popup.tsx";
import { useAuth } from "../auth/auth-context.tsx";
import { UserRole } from "@/lib/api/user.ts";

export const FloatingChat = () => {
  const { user } = useAuth();
  const shouldShowFloatingChat = useShouldShowFloatingChat();
  const isMentorOpsSupportEnabled = useFeatureFlagEnabled(
    "enable-mentor-ops-support",
  );

  const mentorView = useMentorView((state) => state.mentorView);
  const { pathname } = useLocation();

  const isEscalationTriggerVisible =
    (pathname === "/conversations" &&
      (user?.role === UserRole.MENTOR ? true : mentorView)) ||
    pathname?.includes("mentorships");

  const { chatPopupOpen, escalationPopupOpen, setChatPopupOpen } =
    usePopupStore();

  useActiveCallNotification();

  const supervisorUnreadMessage = useGlobalChats(
    useCallback((state) => state.supervisorUnreadMessage, []),
  );
  const setSupervisorUnreadMessage = useGlobalChats(
    useCallback((state) => state.setSupervisorUnreadMessage, []),
  );

  const handleClick = () => {
    setChatPopupOpen(!chatPopupOpen);
    setSupervisorUnreadMessage(false);
  };

  if (isMentorOpsSupportEnabled) return null;
  if (!shouldShowFloatingChat) return null;

  return (
    <div
      className={cn("fixed bottom-4 z-50")}
      style={{
        transition: "right 0.3s ease-in-out",
        right:
          escalationPopupOpen && chatPopupOpen
            ? "32.5rem"
            : isEscalationTriggerVisible
              ? "5rem"
              : "1rem",
      }}
    >
      {/* Only render ChatPopup when it's actually open */}
      {chatPopupOpen && (
        <ChatPopup
          onClose={() => setChatPopupOpen(false)}
          isOpen={chatPopupOpen}
          position={"right"}
          isEscalationTriggerVisible={isEscalationTriggerVisible}
        />
      )}

      <div className={`relative ${!chatPopupOpen && "cursor-move"}`}>
        {supervisorUnreadMessage && !chatPopupOpen && (
          <div className="absolute left-[2px] top-[2px] h-3 w-3 rounded-full bg-red-500" />
        )}
        <Button
          onClick={handleClick}
          size="icon"
          className="h-14 w-14 rounded-full bg-white text-slate-900 shadow-lg hover:bg-slate-100"
        >
          {chatPopupOpen ? (
            <X className="h-6 w-6" />
          ) : (
            <ChatBubbleIcon className="h-6 w-6" />
          )}
        </Button>
      </div>
    </div>
  );
};
